/**
 * 管理后台：权限点 / 分组授权 / 用户授权 / 审计与权限预览
 */
import { useEffect, useState } from 'react';
import { Modal, Form } from 'antd';
import { message } from '../utils/antdMessage';
import { resolveRoleFromGroupNames } from '../utils/systemRoles';

export default function useAdminPermissions({
  apiRequest,
  groups,
  fetchUsers,
  fetchGroups
}) {
  const [permissions, setPermissions] = useState([]);
  const [permForm] = Form.useForm();
  const [permModalVisible, setPermModalVisible] = useState(false);
  const [editingPerm, setEditingPerm] = useState(null);
  const [permSearch, setPermSearch] = useState('');

  const [groupAuthId, setGroupAuthId] = useState(null);
  const [groupAuthPerms, setGroupAuthPerms] = useState([]);
  const [groupAuthMsg, setGroupAuthMsg] = useState('');

  const [userAuthId, setUserAuthId] = useState(null);
  const [userAuthGroups, setUserAuthGroups] = useState([]);
  const [userAuthMsg, setUserAuthMsg] = useState('');
  const [userPerms, setUserPerms] = useState([]);

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditSetupMessage, setAuditSetupMessage] = useState('');
  const [permissionPreview, setPermissionPreview] = useState(null);
  const [permissionPreviewUserId, setPermissionPreviewUserId] = useState(null);

  // 队员可见范围配置Modal
  const [memberScopeModalVisible, setMemberScopeModalVisible] = useState(false);
  const [memberScopeUserId, setMemberScopeUserId] = useState(null);
  const [memberScopeUserName, setMemberScopeUserName] = useState('');

  const fetchPermissions = async () => {
    const data = await apiRequest('/api/admin/permissions');
    if (data.success) setPermissions(data.permissions);
  };
  const handleSavePerm = async (values) => {
    let url, method;
    if (editingPerm) {
      url = `/api/admin/permissions/${editingPerm.id}`;
      method = 'PUT';
    } else {
      url = '/api/admin/permissions';
      method = 'POST';
    }
    const data = await apiRequest(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
    if (data.success) {
      message.success('保存成功');
      setPermModalVisible(false);
      setEditingPerm(null);
      permForm.resetFields();
      fetchPermissions();
    } else {
      message.error(data.error || '保存失败');
    }
  };
  const handleDeletePerm = async (id) => {
    Modal.confirm({
      title: '确定要删除该权限点吗？',
      onOk: async () => {
        const data = await apiRequest(`/api/admin/permissions/${id}`, { method: 'DELETE' });
        if (data.success) fetchPermissions();
      }
    });
  };
  const handleEditPerm = (perm) => {
    setEditingPerm(perm);
    setPermModalVisible(true);
    permForm.setFieldsValue(perm);
  };

  // ========== 分组授权API ==========
  const fetchGroupAuthPerms = async (groupId) => {
    setGroupAuthId(groupId);
    const data = await apiRequest(`/api/admin/group_permissions/${groupId}`);
    if (data.success) setGroupAuthPerms(data.permissions.map(p => p.id));
  };
  // 分组授权保存
  const handleSaveGroupAuth = async () => {
    if (!groupAuthId) return setGroupAuthMsg('请选择分组');
    // 强制转换为数字数组
    const permissionIds = groupAuthPerms.map(id => Number(id));
    const data = await apiRequest('/api/admin/group_permissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupId: groupAuthId, permissionIds })
    });
    if (data.success) {
      setGroupAuthMsg('保存成功');
      fetchGroups();
      fetchPermissions();
    } else {
      setGroupAuthMsg(data.error || '保存失败');
    }
  };

  // ========== 用户授权API ==========
  const fetchUserAuthGroups = async (userId) => {
    setUserAuthId(userId);
    const data = await apiRequest(`/api/admin/user_groups/${userId}`);
    if (data.success) setUserAuthGroups(data.groups.map(g => g.id));
    // 查询用户所有权限
    const permData = await apiRequest(`/api/admin/user_permissions/${userId}`);
    if (permData.success) setUserPerms(permData.permissions);
  };
  // 用户授权保存
  const handleSaveUserAuth = async () => {
    if (!userAuthId) return setUserAuthMsg('请选择用户');
    // 强制转换为数字数组
    const groupIds = userAuthGroups.map(id => Number(id));
    const data = await apiRequest('/api/admin/user_groups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: userAuthId, groupIds })
    });
    // 按分组名同步系统角色：admin > manager > captain > leader > user
    const selectedGroupNames = groups
      .filter((g) => groupIds.includes(g.id))
      .map((g) => g.name);
    const newRole = resolveRoleFromGroupNames(selectedGroupNames);
    await apiRequest('/api/admin/users/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: userAuthId, role: newRole })
    });
    if (data.success) {
      setUserAuthMsg('保存成功');
      fetchUsers();
      fetchGroups();
    } else {
      setUserAuthMsg(data.error || '保存失败');
    }
  };

  const fetchAuditLogs = async () => {
    setAuditLogsLoading(true);
    try {
      const data = await apiRequest('/api/admin/audit-logs');
      if (data.success) {
        setAuditLogs(data.logs || []);
        setAuditSetupMessage(data.setup_required ? (data.message || '') : '');
      } else {
        message.error(data.error || '获取审计日志失败');
      }
    } catch (e) {
      message.error('获取审计日志失败');
    } finally {
      setAuditLogsLoading(false);
    }
  };

  const fetchPermissionPreview = async (userId) => {
    setPermissionPreviewUserId(userId);
    try {
      const data = await apiRequest(`/api/admin/users/${userId}/effective-permissions`);
      if (data.success) {
        setPermissionPreview(data);
      } else {
        message.error(data.error || '获取权限预览失败');
        setPermissionPreview(null);
      }
    } catch (e) {
      message.error('获取权限预览失败');
      setPermissionPreview(null);
    }
  };

  // 打开队员可见范围配置弹窗
  const openMemberScopeModal = (userId, userName) => {
    setMemberScopeUserId(userId);
    setMemberScopeUserName(userName);
    setMemberScopeModalVisible(true);
  };

  // 关闭队员可见范围配置弹窗
  const closeMemberScopeModal = () => {
    setMemberScopeModalVisible(false);
    setMemberScopeUserId(null);
    setMemberScopeUserName('');
  };

  useEffect(() => {
    if (groupAuthId) fetchGroupAuthPerms(groupAuthId);
  }, [groupAuthId]);

  useEffect(() => {
    if (userAuthId) fetchUserAuthGroups(userAuthId);
  }, [userAuthId]);

  return {
    permissions, permForm, permModalVisible, setPermModalVisible, editingPerm, setEditingPerm, permSearch, setPermSearch,
    fetchPermissions, handleSavePerm, handleEditPerm, handleDeletePerm,
    groupAuthId, setGroupAuthId, groupAuthPerms, setGroupAuthPerms, groupAuthMsg, fetchGroupAuthPerms, handleSaveGroupAuth,
    userAuthId, userAuthGroups, setUserAuthGroups, userAuthMsg, userPerms, fetchUserAuthGroups, handleSaveUserAuth,
    auditLogs, auditLogsLoading, auditSetupMessage, permissionPreview, permissionPreviewUserId, setPermissionPreviewUserId,
    fetchAuditLogs, fetchPermissionPreview,
    // 队员可见范围配置
    memberScopeModalVisible, memberScopeUserId, memberScopeUserName,
    openMemberScopeModal, closeMemberScopeModal
  };
}
